import React, { useState } from 'react';
import { Plus, Pill, Clock, Calendar, AlertCircle, CheckCircle, Smartphone, Brain, Search, X, Database } from 'lucide-react';
import ComingSoonBanner from './common/ComingSoonBanner';
import MockDataBanner from './common/MockDataBanner';
import { useMedications } from '../hooks/useSupabaseData';
import { useMockDataDetection } from '../hooks/useMockDataDetection';
import { fdaApi, type FDAMedication } from '../lib/fdaApi';

export const MedicationManager: React.FC = () => {
  const { medications, addMedication, loading, error } = useMedications();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showFDASearch, setShowFDASearch] = useState(false);
  const [fdaSearchQuery, setFDASearchQuery] = useState('');
  const [fdaResults, setFDAResults] = useState<FDAMedication[]>([]);
  const [fdaSearchLoading, setFDASearchLoading] = useState(false);
  const [selectedFDAMed, setSelectedFDAMed] = useState<FDAMedication | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    dosage: '',
    frequency: '',
    instructions: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    is_active: true,
    adherence_percentage: 100
  });

  const mockDataInfo = useMockDataDetection('medications');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50';
      case 'paused': return 'text-yellow-600 bg-yellow-50';
      case 'completed': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getAdherenceColor = (adherence: number) => {
    if (adherence >= 90) return 'text-green-600';
    if (adherence >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = await addMedication(formData);
    if (result) {
      setFormData({
        name: '',
        dosage: '',
        frequency: '',
        instructions: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        is_active: true,
        adherence_percentage: 100
      });
      setShowAddForm(false);
      setSelectedFDAMed(null);
    }
  };

  const searchFDAMedications = async () => {
    if (!fdaSearchQuery.trim()) return;
    
    setFDASearchLoading(true);
    try {
      const result = await fdaApi.searchMedications(fdaSearchQuery, 10);
      setFDAResults(result.results);
    } catch (error) {
      console.error('FDA search error:', error);
    } finally {
      setFDASearchLoading(false);
    }
  };

  const selectFDAMedication = (med: FDAMedication) => {
    setSelectedFDAMed(med);
    setFormData(prev => ({
      ...prev,
      name: med.product_name || med.generic_name || '',
      dosage: med.strength || ''
    }));
    setShowFDASearch(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      dosage: '',
      frequency: '',
      instructions: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      is_active: true,
      adherence_percentage: 100
    });
    setSelectedFDAMed(null);
    setShowAddForm(false);
    setShowFDASearch(false);
    setFDAResults([]);
    setFDASearchQuery('');
  };

  const handleStartOnboarding = () => {
    window.dispatchEvent(new CustomEvent('start-onboarding'));
  };

  const handleAddData = () => {
    window.dispatchEvent(new CustomEvent('showAddMedication'));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mock Data Banner */}
      {mockDataInfo.shouldShowBanner && (
        <MockDataBanner
          dataType="medications"
          onStartOnboarding={handleStartOnboarding}
          onAddData={handleAddData}
          variant="info"
        />
      )}
      {/* Coming Soon Banner */}
      <ComingSoonBanner 
        featureName="Smart Medication Manager"
        description="Your medication tracker will sync with your pharmacy, send real smart reminders, provide adherence analytics, and integrate with your health devices for optimal medication management."
        currentPage="/medications"
        variant="compact"
        expectedRelease="very soon"
      />
      
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <img 
              src="/zippy-logo-simple.svg" 
              alt="ZippyHealth Logo"
              className="w-8 h-8"
            />
            <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
              Medication Manager
            </h2>
          </div>
          <p className="text-slate-600 mt-1">Smart medication tracking with ZippyHealth.io AI reminders</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all transform hover:scale-105">
            <Brain className="h-4 w-4" />
            <span>AI Insights</span>
          </button>
          <button 
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all transform hover:scale-105"
          >
            <Plus className="h-4 w-4" />
            <span>Add Medication</span>
          </button>
        </div>
      </div>

      {/* Quick Add Medication Form */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Add New Medication</h3>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* FDA Search Section */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Database className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-blue-900">Search FDA Database</h4>
              </div>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={fdaSearchQuery}
                  onChange={(e) => setFDASearchQuery(e.target.value)}
                  placeholder="Search for medication (e.g., Metformin, Lisinopril)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                  onKeyPress={(e) => e.key === 'Enter' && searchFDAMedications()}
                />
                <button
                  onClick={searchFDAMedications}
                  disabled={fdaSearchLoading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {fdaSearchLoading ? 'Searching...' : 'Search'}
                </button>
              </div>

              {selectedFDAMed && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-3">
                  <div className="flex items-center gap-2 text-green-800">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">Selected from FDA Database:</span>
                  </div>
                  <div className="mt-1 text-green-700">
                    <strong>{selectedFDAMed.product_name}</strong>
                    {selectedFDAMed.generic_name && selectedFDAMed.generic_name !== selectedFDAMed.product_name && (
                      <span> ({selectedFDAMed.generic_name})</span>
                    )}
                    {selectedFDAMed.strength && <span> - {selectedFDAMed.strength}</span>}
                    {selectedFDAMed.manufacturer && <span> by {selectedFDAMed.manufacturer}</span>}
                  </div>
                </div>
              )}

              {fdaResults.length > 0 && (
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                  {fdaResults.map((med, index) => (
                    <button
                      key={`${med.ndc_code}-${index}`}
                      onClick={() => selectFDAMedication(med)}
                      className="w-full text-left p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900">{med.product_name}</div>
                      {med.generic_name && med.generic_name !== med.product_name && (
                        <div className="text-sm text-gray-600">Generic: {med.generic_name}</div>
                      )}
                      <div className="text-sm text-gray-500">
                        {med.strength && <span>{med.strength}</span>}
                        {med.dosage_form && <span> • {med.dosage_form}</span>}
                        {med.manufacturer && <span> • {med.manufacturer}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Medication Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter medication name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dosage *
                  </label>
                  <input
                    type="text"
                    name="dosage"
                    value={formData.dosage}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 500mg, 10ml"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Frequency *
                  </label>
                  <select
                    name="frequency"
                    value={formData.frequency}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select frequency</option>
                    <option value="Once daily">Once daily</option>
                    <option value="Twice daily">Twice daily</option>
                    <option value="Three times daily">Three times daily</option>
                    <option value="Four times daily">Four times daily</option>
                    <option value="Every other day">Every other day</option>
                    <option value="Weekly">Weekly</option>
                    <option value="As needed">As needed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date (Optional)
                  </label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adherence Percentage
                  </label>
                  <input
                    type="number"
                    name="adherence_percentage"
                    value={formData.adherence_percentage}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instructions
                </label>
                <textarea
                  name="instructions"
                  value={formData.instructions}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Take with food, Avoid alcohol"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Currently taking this medication
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add Medication
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Loading and Error States */}
      {loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-700">Loading your medications...</p>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Error loading medications. Please try again.</p>
        </div>
      )}

      {/* Smart Reminders Integration */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Smartphone className="h-6 w-6 text-red-600" />
          <h3 className="text-lg font-semibold text-slate-800">ZippyHealth Smart Reminders</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <h4 className="font-medium text-slate-800 mb-2">Next Dose Alert</h4>
            <p className="text-sm text-slate-600">Metformin 500mg in 30 minutes</p>
            <div className="flex items-center space-x-2 mt-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-green-600">Active reminder</span>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <h4 className="font-medium text-slate-800 mb-2">Adherence Score</h4>
            <p className="text-2xl font-bold text-green-600">92%</p>
            <p className="text-xs text-slate-500">Last 30 days</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <h4 className="font-medium text-slate-800 mb-2">AI Optimization</h4>
            <p className="text-sm text-slate-600">Timing suggestions available</p>
            <button className="text-xs text-red-600 hover:text-red-800 font-medium mt-1">
              View recommendations
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Active Medications</p>
              <p className="text-2xl font-bold text-slate-800">
                {medications.filter(m => m.status === 'active').length}
              </p>
            </div>
            <Pill className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Next Dose</p>
              <p className="text-2xl font-bold text-slate-800">30 min</p>
            </div>
            <Clock className="h-8 w-8 text-orange-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Adherence Rate</p>
              <p className="text-2xl font-bold text-slate-800">92%</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Missed Doses</p>
              <p className="text-2xl font-bold text-slate-800">2</p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Medications List */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-slate-800">Your Medications</h3>
            <img 
              src="/favicon-96x96.png" 
              alt="Zippy" 
              className="w-4 h-4 opacity-60"
            />
          </div>
        </div>
        <div className="divide-y divide-slate-200">
          {medications.map((medication) => (
            <div key={medication.id} className="p-6 hover:bg-slate-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-lg font-medium text-slate-800">{medication.name}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(medication.is_active ? 'active' : 'paused')}`}>
                      {medication.is_active ? 'active' : 'paused'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-600 mb-3">
                    <div>
                      <span className="font-medium">Dosage:</span> {medication.dosage}
                    </div>
                    <div>
                      <span className="font-medium">Frequency:</span> {medication.frequency}
                    </div>
                    <div>
                      <span className="font-medium">Started:</span> {new Date(medication.start_date).toLocaleDateString()}
                    </div>
                  </div>
                  {medication.instructions && (
                    <p className="text-sm text-slate-600 mb-2">{medication.instructions}</p>
                  )}
                  <div className="flex items-center space-x-4">
                    {medication.adherence_percentage && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-slate-500">Adherence:</span>
                        <span className={`text-sm font-medium ${getAdherenceColor(medication.adherence_percentage)}`}>
                          {medication.adherence_percentage}%
                        </span>
                      </div>
                    )}
                    {medication.prescribing_provider && (
                      <div className="flex items-center space-x-1 text-sm text-slate-500">
                        <span>Provider: {medication.prescribing_provider}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="px-3 py-1 text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                    Take Now
                  </button>
                  <button className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    Edit
                  </button>
                </div>
              </div>
            </div>
          ))}
          {medications.length === 0 && !loading && (
            <div className="p-12 text-center">
              <Pill className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-800 mb-2">No medications added yet</h3>
              <p className="text-slate-600 mb-4">Start tracking your medications for better adherence and health outcomes.</p>
              <button 
                onClick={() => setShowAddForm(true)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
              >
                Add Your First Medication
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
